import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGetInvestorRisksQuery, type RiskRecord } from '@/store/insightsApi';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import dayjs from '@/config/dayjs';
import type { TFunction } from 'i18next';
import { useLocalization } from '@/hooks/useLocalization';

type RisksListProps = {
  investorId: number;
  lastLogin?: string | null;
  lastInviteDate?: string | null;
};

// Helper to render risk text based on Code
const formatRiskText = (
  t: TFunction,
  r: RiskRecord,
  lastLogin?: string | null,
  lastInviteDate?: string | null,
) => {
  const code = (r.Code || '').toLowerCase();
  if (code.includes('inactive') || code.includes('90')) {
    const date = lastLogin
      ? dayjs(lastLogin).format('M/D/YY')
      : t('unknown', 'Unknown');
    return `${r.Name}: ${t('lastLogin', 'last login')} ${date}`;
  }
  if (code.includes('invitation')) {
    const date = lastInviteDate
      ? dayjs(lastInviteDate).format('M/D/YY')
      : t('unknown', 'Unknown');
    return `${r.Name}: ${t('invitationSent', 'invitation sent')} ${date}`;
  }
  return r.Name;
};

export default function RisksList({
  investorId,
  lastLogin,
  lastInviteDate,
}: RisksListProps) {
  const { t } = useLocalization();
  const authLoaded = useAuthIsLoaded();

  const {
    data: risksData,
    isLoading: risksLoading,
    isFetching: risksFetching,
  } = useGetInvestorRisksQuery(investorId, {
    skip: !authLoaded || !investorId,
  });

  if (risksLoading || risksFetching || !authLoaded) return <LoadingSpinner />;

  const risks = (risksData?.Records || []) as RiskRecord[];

  return (
    <Box>
      <Typography
        variant='subtitle1'
        sx={{ mb: 1 }}
      >
        {t('risks', 'Risks')}
      </Typography>
      {risks.length === 0 ? (
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {t(
            'noRisksForClient',
            'There are no risks currently flagged for this client.',
          )}
        </Typography>
      ) : (
        <List
          dense
          sx={{ listStyleType: 'disc', pl: 3 }}
        >
          {risks.map((r) => (
            <ListItem
              key={r.RiskId}
              sx={{ display: 'list-item', py: 0 }}
            >
              <ListItemText
                primary={formatRiskText(
                  t,
                  r,
                  lastLogin || undefined,
                  lastInviteDate || undefined,
                )}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
